import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Stack,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MainLayout from '../../../components/layout/MainLayout';
import { ColorPicker } from '../../../shared/buttonEditor';
import { useAuth } from '../../../contexts/AuthContext';
import { ROUTES } from '../../../utils/constants';
import {
  getTouchPanel,
  saveTouchPanel,
  getPanelContentFingerprint,
} from '../../../services/touchPanelService';
import { listAssignmentsForPanel } from '../../../services/touchPanelAssignmentService';
import { fetchAgents } from '../../../api/agentApi';
import { extractDeviceId } from '../../../services/touchPanelDeployable/eligibility';
import ScreenCanvas from './ScreenCanvas';
import TouchPanelButtonModal from './TouchPanelButtonModal';
import AssignmentSummaryCard from './AssignmentSummaryCard';
import AssignDevicesDialog from './AssignDevicesDialog';
import {
  createDefaultButton,
  getScreenById,
  getSortedScreens,
  resolveScreenBackground,
  resolveNavigation,
  updateScreenInPanel,
  renameScreenInPanel,
  deleteScreenFromPanel,
  addScreenToPanel,
  moveButtonOnScreen,
  PANEL_BG_PRESETS,
  clampScreenGridDimension,
} from './panelModel';
import { TOUCH_PANEL_PAYLOAD_LIMITS } from '../../../services/touchPanelDeployable/payloadLimits';
import {
  prepareTouchPanelDeployment,
} from '../../../services/touchPanelDeploymentService';
import { deliverTouchPanelDeployment } from '../../../api/touchPanelDeployApi';


const AUTOSAVE_MS = 2000;
const DEPLOY_APPLY_POLL_MS = 2000;
const DEPLOY_APPLY_POLLS = 8;

function sleep(ms) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function formatDeployError(err) {
  if (!err) return 'Deploy failed';
  if (err.code === 'VALIDATION_FAILED') {
    const first = err.validation?.errors?.[0];
    if (first) return `Deploy blocked: ${first.code} — ${first.message}`;
    return 'Deploy blocked — fix validation errors first.';
  }
  return (
    err.response?.data?.error ||
    err.message ||
    err.error ||
    'Deploy failed'
  );
}

function deviceLabel(assignment, deviceId) {
  return assignment?.deviceSnapshot?.name || deviceId;
}

/**
 * Touch Panel Designer — Firestore editor with Phase 4 assignment / prepare.
 * Reuses Phase 2 canvas / screen / button editing UI.
 */
export default function TouchPanelDesignerPage() {
  const { panelId } = useParams();
  const navigate = useNavigate();
  const { userData, userClaims, currentUser, tenantId: authTenantId } = useAuth();

  const tenantId = authTenantId || userData?.tenantId || userClaims?.tenantId;
  const userId = currentUser?.uid || null;

  const [panel, setPanel] = useState(null);
  const [savedFingerprint, setSavedFingerprint] = useState('');
  const [currentScreenId, setCurrentScreenId] = useState(null);
  const [navHistory, setNavHistory] = useState([]);
  const [editingButton, setEditingButton] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saveState, setSaveState] = useState('saved'); // saved | saving | unsaved
  const [saveError, setSaveError] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [prepareError, setPrepareError] = useState(null);
  const [deploying, setDeploying] = useState(false);
  const [deployError, setDeployError] = useState(null);
  const [deployMessage, setDeployMessage] = useState(null);
  const [deploySeverity, setDeploySeverity] = useState('info');
  const [devicePresence, setDevicePresence] = useState({});

  const savingRef = useRef(false);
  const deployLockRef = useRef(false);
  const panelRef = useRef(panel);
  panelRef.current = panel;

  const isDirty = useMemo(() => {
    if (!panel || !savedFingerprint) return false;
    return getPanelContentFingerprint(panel) !== savedFingerprint;
  }, [panel, savedFingerprint]);

  useEffect(() => {
    if (!panel) return;
    if (savingRef.current) return;
    setSaveState(isDirty ? 'unsaved' : 'saved');
  }, [isDirty, panel]);

  const flash = (msg) => {
    setStatusMessage(msg);
    window.setTimeout(() => setStatusMessage(''), 2500);
  };

  const loadPanel = useCallback(async () => {
    if (!tenantId || !panelId) {
      setLoading(false);
      setLoadError(!tenantId ? 'No tenant selected' : 'Missing panel id');
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      const [doc, assigns] = await Promise.all([
        getTouchPanel(tenantId, panelId),
        listAssignmentsForPanel(tenantId, panelId).catch(() => []),
      ]);
      if (!doc) {
        setLoadError('Touch panel not found');
        setPanel(null);
        return;
      }
      setPanel(doc);
      setAssignments(assigns || []);
      setSavedFingerprint(getPanelContentFingerprint(doc));
      setCurrentScreenId(doc.defaultScreenId || doc.screens?.[0]?.id || null);
      setNavHistory([]);
      setSaveState('saved');
    } catch (err) {
      console.error('[TouchPanelDesigner] load failed', err);
      setLoadError(err.message || 'Failed to load panel');
      setPanel(null);
    } finally {
      setLoading(false);
    }
  }, [tenantId, panelId]);

  const refreshAssignments = useCallback(async () => {
    if (!tenantId || !panelId) return;
    try {
      const assigns = await listAssignmentsForPanel(tenantId, panelId);
      setAssignments(assigns || []);
      try {
        const response = await fetchAgents({ tenant_id: tenantId, tenantId, limit: 100 });
        const agents =
          response?.data?.agents ||
          response?.agents ||
          response?.data?.items ||
          response?.items ||
          [];
        const next = {};
        (Array.isArray(agents) ? agents : []).forEach((agent) => {
          const id = extractDeviceId(agent);
          if (!id) return;
          const raw = String(agent.status || agent.healthStatus || '').toLowerCase();
          next[id] =
            raw === 'online' || raw === 'idle' || raw === 'busy' || raw === 'ready'
              ? 'online'
              : raw
                ? 'offline'
                : 'unknown';
        });
        setDevicePresence(next);
      } catch (presenceErr) {
        console.warn('[TouchPanelDesigner] agent presence refresh failed', presenceErr);
      }
      return assigns || [];
    } catch (err) {
      console.error('[TouchPanelDesigner] assignment refresh failed', err);
      return [];
    }
  }, [tenantId, panelId]);

  const handlePrepareDeployment = useCallback(async () => {
    if (!tenantId || !panel?.id || preparing) return;
    setPreparing(true);
    setPrepareError(null);
    try {
      await prepareTouchPanelDeployment(tenantId, {
        panelId: panel.id,
        userId,
        confirmWarnings: true,
      });
      await refreshAssignments();
      flash('Deployment prepared (not sent)');
    } catch (err) {
      console.error('[TouchPanelDesigner] prepare failed', err);
      if (err.code === 'VALIDATION_FAILED') {
        const first = err.validation?.errors?.[0];
        setPrepareError(
          first
            ? `Prepare failed: ${first.code} — ${first.message}`
            : 'Prepare failed — fix validation errors first.'
        );
      } else {
        setPrepareError(err.message || 'Prepare failed');
      }
    } finally {
      setPreparing(false);
    }
  }, [tenantId, panel?.id, preparing, userId, refreshAssignments]);

  useEffect(() => {
    loadPanel();
  }, [loadPanel]);

  const performSave = useCallback(
    async ({ silent = false } = {}) => {
      const current = panelRef.current;
      if (!tenantId || !current?.id) return null;
      const fingerprintAtSave = getPanelContentFingerprint(current);
      if (fingerprintAtSave === savedFingerprint) {
        setSaveState('saved');
        return current;
      }
      if (savingRef.current) {
        // Wait briefly for an in-flight autosave instead of failing Deploy.
        for (let i = 0; i < 25; i += 1) {
          await sleep(200);
          if (!savingRef.current) break;
        }
        if (savingRef.current) return null;
      }

      const latest = panelRef.current;
      if (!latest?.id) return null;
      const fingerprintNow = getPanelContentFingerprint(latest);
      if (fingerprintNow === savedFingerprint) {
        setSaveState('saved');
        return latest;
      }

      savingRef.current = true;
      setSaveState('saving');
      setSaveError(null);
      try {
        const saved = await saveTouchPanel(tenantId, latest, userId);
        const stillSame = getPanelContentFingerprint(panelRef.current) === fingerprintNow;
        if (stillSame) {
          setPanel(saved);
          setSavedFingerprint(getPanelContentFingerprint(saved));
          setSaveState('saved');
        } else {
          // Keep newer local edits; adopt server revision/audit only.
          setPanel((prev) => ({
            ...prev,
            revision: saved.revision,
            updatedAt: saved.updatedAt,
            updatedBy: saved.updatedBy,
            createdAt: saved.createdAt,
            createdBy: saved.createdBy,
          }));
          setSavedFingerprint(getPanelContentFingerprint(saved));
          setSaveState('unsaved');
        }
        if (!silent) flash('Panel saved');
        return saved;
      } catch (err) {
        console.error('[TouchPanelDesigner] save failed', err);
        setSaveError(err.message || 'Save failed');
        setSaveState('unsaved');
        if (!silent) flash(err.message || 'Save failed');
        return null;
      } finally {
        savingRef.current = false;
      }
    },
    [tenantId, userId, savedFingerprint]
  );

  const handleDeploy = useCallback(async () => {
    if (!tenantId || !panel?.id || deploying || preparing || deployLockRef.current) return;
    const assigned = (assignments || []).filter((a) => a && a.enabled !== false);
    const assignedIds = assigned.map((a) => a.deviceId).filter(Boolean);
    if (assignedIds.length === 0) {
      setDeployError('Assign at least one device first, then Deploy.');
      setDeployMessage(null);
      return;
    }

    deployLockRef.current = true;
    setDeploying(true);
    setDeployError(null);
    setDeployMessage(null);
    setDeploySeverity('info');
    setPrepareError(null);
    try {
      // Always flush pending edits; wait out autosave so prepare/deliver share one revision.
      const saved = await performSave({ silent: true });
      if (isDirty && !saved) {
        throw new Error('Could not save panel before deploy. Wait a second and try again.');
      }

      const prepared = await prepareTouchPanelDeployment(tenantId, {
        panelId: panel.id,
        userId,
        confirmWarnings: true,
      });
      const deviceIds =
        (prepared?.targetDeviceIds || []).length > 0
          ? [...prepared.targetDeviceIds]
          : assignedIds;
      const targetRev = Number(prepared.panelRevision);

      const result = await deliverTouchPanelDeployment(prepared.id, {
        tenantId,
        deviceIds,
      });

      const targets = Array.isArray(result?.targets) ? result.targets : [];
      const byId = new Map(assigned.map((a) => [a.deviceId, a]));

      // Poll until devices report applied / failed, or timeout.
      let latestAssigns = await refreshAssignments();
      for (let i = 0; i < DEPLOY_APPLY_POLLS; i += 1) {
        const pending = deviceIds.filter((id) => {
          const a = (latestAssigns || []).find((row) => row.deviceId === id);
          if (!a) return true;
          if (Number(a.deployedPanelRevision) === targetRev && a.lastDeploymentStatus === 'succeeded') {
            return false;
          }
          if (['failed', 'timedOut', 'deploymentFailed'].includes(a.lastDeploymentStatus)) {
            return false;
          }
          const t = targets.find((row) => row.deviceId === id);
          // Offline queue will not apply while the agent stays down — stop waiting.
          if (t?.status === 'queued') return false;
          if (t?.status === 'failed') return false;
          return true;
        });
        if (pending.length === 0) break;
        await sleep(DEPLOY_APPLY_POLL_MS);
        latestAssigns = await refreshAssignments();
      }

      if (panel?.id) {
        const fresh = await getTouchPanel(tenantId, panel.id);
        if (fresh) {
          setPanel(fresh);
          setSavedFingerprint(getPanelContentFingerprint(fresh));
        }
      }

      const lines = deviceIds.map((id) => {
        const a = (latestAssigns || []).find((row) => row.deviceId === id) || byId.get(id);
        const name = deviceLabel(a, id);
        const t = targets.find((row) => row.deviceId === id);
        const presence = devicePresence[id];
        if (Number(a?.deployedPanelRevision) === targetRev && a?.lastDeploymentStatus === 'succeeded') {
          return `${name}: applied (rev ${targetRev})`;
        }
        if (t?.status === 'failed' || a?.lastDeploymentStatus === 'failed') {
          const err =
            t?.error?.message ||
            t?.error?.code ||
            a?.lastDeploymentError?.message ||
            a?.lastDeploymentError?.code ||
            'failed';
          return `${name}: failed — ${err}`;
        }
        if (t?.status === 'queued' || presence === 'offline') {
          return `${name}: OFFLINE — bring panel online, then Deploy again`;
        }
        if (t?.status === 'sent' || a?.lastDeploymentStatus === 'deploying') {
          return `${name}: sent — waiting for panel to confirm`;
        }
        return `${name}: ${t?.status || a?.lastDeploymentStatus || 'unknown'}`;
      });

      const applied = lines.filter((l) => l.includes(': applied')).length;
      const offline = lines.filter((l) => l.includes('OFFLINE')).length;
      const failed = lines.filter((l) => l.includes(': failed')).length;
      const summary = lines.join('\n');

      if (failed > 0 && applied === 0) {
        setDeploySeverity('error');
        setDeployError(summary);
        setDeployMessage(null);
        window.alert(`Deploy failed\n\n${summary}`);
      } else if (offline > 0 || failed > 0 || applied < deviceIds.length) {
        setDeploySeverity('warning');
        setDeployMessage(summary);
        setDeployError(null);
        window.alert(`Deploy incomplete\n\n${summary}`);
      } else {
        setDeploySeverity('success');
        setDeployMessage(summary);
        setDeployError(null);
      }
    } catch (err) {
      console.error('[TouchPanelDesigner] deploy failed', err);
      const msg = formatDeployError(err);
      setDeploySeverity('error');
      setDeployError(msg);
      setDeployMessage(null);
      window.alert(msg);
    } finally {
      deployLockRef.current = false;
      setDeploying(false);
    }
  }, [
    tenantId,
    panel?.id,
    deploying,
    preparing,
    assignments,
    isDirty,
    userId,
    performSave,
    refreshAssignments,
    devicePresence,
  ]);

  // Autosave when dirty
  useEffect(() => {
    if (!isDirty || !panel || loading || deployLockRef.current || deploying) return undefined;
    const timer = window.setTimeout(() => {
      if (deployLockRef.current) return;
      performSave({ silent: true });
    }, AUTOSAVE_MS);
    return () => window.clearTimeout(timer);
  }, [isDirty, panel, loading, performSave, deploying]);

  // Warn on browser close with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (!isDirty) return undefined;
      e.preventDefault();
      e.returnValue = 'You have unsaved changes.';
      return e.returnValue;
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  const screens = useMemo(() => (panel ? getSortedScreens(panel) : []), [panel]);
  const currentScreen = panel ? getScreenById(panel, currentScreenId) || screens[0] : null;
  const canvasBg = panel ? resolveScreenBackground(panel, currentScreen) : '#0f172a';

  const ensureScreenSelected = useCallback((nextPanel, preferredId) => {
    const id =
      preferredId && getScreenById(nextPanel, preferredId)
        ? preferredId
        : nextPanel.defaultScreenId || getSortedScreens(nextPanel)[0]?.id;
    setCurrentScreenId(id);
  }, []);

  const handlePanelField = (field, value) => {
    setPanel((prev) => ({ ...prev, [field]: value }));
  };

  const handleBackgroundColor = (color) => {
    setPanel((prev) => ({
      ...prev,
      appearance: { ...prev.appearance, backgroundColor: color },
    }));
  };

  const handleSelectScreen = (screenId) => {
    setCurrentScreenId(screenId);
    setNavHistory([]);
  };

  const handleAddScreen = () => {
    setPanel((prev) => {
      const next = addScreenToPanel(prev, `Screen ${prev.screens.length + 1}`);
      const added = next.screens[next.screens.length - 1];
      setCurrentScreenId(added.id);
      return next;
    });
    flash('Screen added');
  };

  const handleRenameScreen = (name) => {
    if (!currentScreen) return;
    setPanel((prev) => renameScreenInPanel(prev, currentScreen.id, name));
  };

  const handleDeleteScreen = () => {
    if (!currentScreen || !panel) return;
    if (panel.screens.length <= 1) {
      flash('Cannot delete the last screen');
      return;
    }
    const ok = window.confirm(
      `Delete screen “${currentScreen.name}”? This cannot be undone.`
    );
    if (!ok) return;
    setPanel((prev) => {
      const next = deleteScreenFromPanel(prev, currentScreen.id);
      ensureScreenSelected(next, next.defaultScreenId);
      setNavHistory([]);
      return next;
    });
    flash('Screen deleted');
  };

  const handleScreenGrid = (key, value) => {
    if (!currentScreen) return;
    const clamped = clampScreenGridDimension(key, value);
    setPanel((prev) =>
      updateScreenInPanel(prev, currentScreen.id, (s) => {
        const next = { ...s, [key]: clamped };
        const max = next.rows * next.columns;
        next.buttons = (s.buttons || []).filter((b) => Number(b.position) < max);
        return next;
      })
    );
  };

  const handleScreenAppearance = (patch) => {
    if (!currentScreen) return;
    setPanel((prev) =>
      updateScreenInPanel(prev, currentScreen.id, (s) => ({
        ...s,
        appearance: { ...s.appearance, ...patch },
      }))
    );
  };

  const handleEmptySlotClick = (position) => {
    if (!currentScreen) return;
    const btn = createDefaultButton({ position, label: 'New Button' });
    setPanel((prev) =>
      updateScreenInPanel(prev, currentScreen.id, (s) => ({
        ...s,
        buttons: [...(s.buttons || []), btn],
      }))
    );
    setEditingButton(btn);
  };

  const handleSaveButton = (changes) => {
    if (!editingButton || !currentScreen) return;
    setPanel((prev) =>
      updateScreenInPanel(prev, currentScreen.id, (s) => ({
        ...s,
        buttons: (s.buttons || []).map((b) =>
          b.id === editingButton.id ? { ...b, ...changes } : b
        ),
      }))
    );
    setEditingButton(null);
    flash('Button saved');
  };

  const handleDeleteButton = (btn) => {
    const target = btn || editingButton;
    if (!target || !currentScreen) return;
    setPanel((prev) =>
      updateScreenInPanel(prev, currentScreen.id, (s) => ({
        ...s,
        buttons: (s.buttons || []).filter((b) => b.id !== target.id),
      }))
    );
    setEditingButton(null);
    flash('Button deleted');
  };

  const handleButtonMove = (fromPosition, toPosition) => {
    if (!currentScreen) return;
    setPanel((prev) =>
      updateScreenInPanel(prev, currentScreen.id, (s) => {
        const next = moveButtonOnScreen(s, fromPosition, toPosition);
        return next || s;
      })
    );
  };

  const handlePreviewButton = (btn) => {
    if (!panel) return;
    const result = resolveNavigation(panel, currentScreenId, btn, navHistory);
    if (result.kind === 'flow') {
      flash(btn.flowId ? `Flow “${btn.flowId}” (local only — not executed)` : 'Flow action (no id)');
      return;
    }
    if (result.kind === 'none') {
      flash('No action');
      return;
    }
    if (result.kind === 'invalid') {
      flash('Open Screen target missing');
      return;
    }
    if (result.pushHistory) {
      setNavHistory((h) => [...h, currentScreenId]);
    }
    if (result.popHistory) {
      setNavHistory((h) => h.slice(0, -1));
    }
    if (result.clearHistory) {
      setNavHistory([]);
    }
    if (result.screenId) {
      setCurrentScreenId(result.screenId);
    }
  };

  const handleBackToList = async () => {
    if (isDirty) {
      const ok = window.confirm('You have unsaved changes. Leave without saving?');
      if (!ok) return;
    }
    navigate(ROUTES.TOUCH_PANEL_DESIGNER);
  };

  const saveStatusChip = (() => {
    if (saveState === 'saving') {
      return <Chip size="small" color="info" label="Saving..." />;
    }
    if (saveState === 'unsaved' || isDirty) {
      return <Chip size="small" color="warning" label="Unsaved Changes" />;
    }
    return <Chip size="small" color="success" variant="outlined" label="Saved" />;
  })();

  if (loading) {
    return (
      <MainLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </MainLayout>
    );
  }

  if (loadError || !panel) {
    return (
      <MainLayout>
        <Box sx={{ maxWidth: 720, mx: 'auto', px: 3, py: 4 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {loadError || 'Panel unavailable'}
          </Alert>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(ROUTES.TOUCH_PANEL_DESIGNER)}>
            Back to Touch Panels
          </Button>
        </Box>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Box
        sx={{
          height: '100%',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          px: { xs: 1.5, md: 2 },
          py: 1,
          pb: 1.5,
          boxSizing: 'border-box',
          gap: 1,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
          <IconButton size="small" onClick={handleBackToList} aria-label="Back to list">
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Typography variant="h6" component="h1">
            Touch Panel Designer
          </Typography>
        </Stack>

        {saveError ? (
          <Alert severity="error" sx={{ flexShrink: 0, py: 0 }} onClose={() => setSaveError(null)}>
            {saveError}
          </Alert>
        ) : null}

        {prepareError ? (
          <Alert severity="error" sx={{ flexShrink: 0, py: 0 }} onClose={() => setPrepareError(null)}>
            {prepareError}
          </Alert>
        ) : null}

        {deployError ? (
          <Alert severity="error" sx={{ flexShrink: 0, py: 0 }} onClose={() => setDeployError(null)}>
            {deployError}
          </Alert>
        ) : null}

        {statusMessage ? (
          <Alert severity="info" sx={{ flexShrink: 0, py: 0 }}>
            {statusMessage}
          </Alert>
        ) : null}

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 300px' },
            gap: 1.5,
            alignItems: 'stretch',
          }}
        >
          <Paper
            sx={{
              minHeight: 0,
              p: 1.5,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              order: { xs: 2, lg: 1 },
            }}
          >
            {panel.branding?.logoUrl ? (
              <Box
                component="img"
                src={panel.branding.logoUrl}
                alt="Brand"
                sx={{
                  maxHeight: 28,
                  mb: 0.5,
                  display: 'block',
                  mx: 'auto',
                  objectFit: 'contain',
                  flexShrink: 0,
                }}
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            ) : null}
            <Box sx={{ flex: 1, minHeight: 0, width: '100%', overflow: 'hidden' }}>
              {currentScreen ? (
                <ScreenCanvas
                  screen={currentScreen}
                  backgroundColor={canvasBg}
                  editMode
                  onEmptySlotClick={handleEmptySlotClick}
                  onButtonClick={handlePreviewButton}
                  onButtonEdit={setEditingButton}
                  onButtonMove={handleButtonMove}
                />
              ) : null}
            </Box>
          </Paper>

          <Stack
            spacing={1.25}
            sx={{
              minHeight: 0,
              overflow: 'auto',
              order: { xs: 1, lg: 2 },
            }}
          >
            <Paper sx={{ p: 1.5 }}>
              <Typography variant="subtitle2" gutterBottom>
                Panel
              </Typography>
              <Stack spacing={1.25}>
                <TextField
                  label="Panel Name"
                  fullWidth
                  size="small"
                  value={panel.name}
                  onChange={(e) => handlePanelField('name', e.target.value)}
                />
                <TextField
                  label="Description"
                  fullWidth
                  size="small"
                  value={panel.description}
                  onChange={(e) => handlePanelField('description', e.target.value)}
                />
                <TextField
                  label="Branding Logo URL"
                  fullWidth
                  size="small"
                  value={panel.branding?.logoUrl || ''}
                  onChange={(e) =>
                    setPanel((prev) => ({
                      ...prev,
                      branding: { ...prev.branding, logoUrl: e.target.value || null },
                    }))
                  }
                />
                <ColorPicker
                  label="Panel background"
                  value={panel.appearance?.backgroundColor}
                  onChange={handleBackgroundColor}
                  presets={PANEL_BG_PRESETS}
                />
                <FormControl fullWidth size="small">
                  <InputLabel id="default-screen-label">Default Screen</InputLabel>
                  <Select
                    labelId="default-screen-label"
                    label="Default Screen"
                    value={panel.defaultScreenId || ''}
                    onChange={(e) => handlePanelField('defaultScreenId', e.target.value)}
                  >
                    {screens.map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        {s.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </Paper>

            <Paper sx={{ p: 1.5 }}>
              <Typography variant="subtitle2" gutterBottom>
                Screen properties
              </Typography>
              {currentScreen ? (
                <Stack spacing={1.25}>
                  <TextField
                    label="Screen Name"
                    fullWidth
                    size="small"
                    value={currentScreen.name}
                    onChange={(e) => handleRenameScreen(e.target.value)}
                  />
                  <Stack direction="row" spacing={1}>
                    <TextField
                      label="Columns"
                      type="number"
                      fullWidth
                      size="small"
                      inputProps={{
                        min: TOUCH_PANEL_PAYLOAD_LIMITS.MIN_COLUMNS,
                        max: TOUCH_PANEL_PAYLOAD_LIMITS.MAX_COLUMNS,
                      }}
                      helperText={`${TOUCH_PANEL_PAYLOAD_LIMITS.MIN_COLUMNS}–${TOUCH_PANEL_PAYLOAD_LIMITS.MAX_COLUMNS}`}
                      value={currentScreen.columns}
                      onChange={(e) => handleScreenGrid('columns', e.target.value)}
                    />
                    <TextField
                      label="Rows"
                      type="number"
                      fullWidth
                      size="small"
                      inputProps={{
                        min: TOUCH_PANEL_PAYLOAD_LIMITS.MIN_ROWS,
                        max: TOUCH_PANEL_PAYLOAD_LIMITS.MAX_ROWS,
                      }}
                      helperText={`${TOUCH_PANEL_PAYLOAD_LIMITS.MIN_ROWS}–${TOUCH_PANEL_PAYLOAD_LIMITS.MAX_ROWS}`}
                      value={currentScreen.rows}
                      onChange={(e) => handleScreenGrid('rows', e.target.value)}
                    />
                  </Stack>
                  <FormControl fullWidth size="small">
                    <InputLabel id="bg-mode-label">Background</InputLabel>
                    <Select
                      labelId="bg-mode-label"
                      label="Background"
                      value={currentScreen.appearance?.backgroundMode || 'inherit'}
                      onChange={(e) => handleScreenAppearance({ backgroundMode: e.target.value })}
                    >
                      <MenuItem value="inherit">Inherit panel</MenuItem>
                      <MenuItem value="override">Override</MenuItem>
                    </Select>
                  </FormControl>
                  {currentScreen.appearance?.backgroundMode === 'override' && (
                    <ColorPicker
                      label="Screen background"
                      value={currentScreen.appearance?.backgroundColor || panel.appearance.backgroundColor}
                      onChange={(c) => handleScreenAppearance({ backgroundColor: c })}
                      presets={PANEL_BG_PRESETS}
                    />
                  )}
                  <Divider />
                  <Button color="error" variant="outlined" size="small" onClick={handleDeleteScreen}>
                    Delete screen
                  </Button>
                  <Typography variant="caption" color="text.secondary">
                    Schema v{panel.schemaVersion} · {panel.id}
                  </Typography>
                </Stack>
              ) : null}
            </Paper>

            <AssignmentSummaryCard
              panel={panel}
              assignments={assignments}
              devicePresence={devicePresence}
              onAssignDevices={() => setAssignOpen(true)}
              onPrepareDeployment={handlePrepareDeployment}
              preparing={preparing}
              onDeploy={handleDeploy}
              deploying={deploying}
              saveStatus={saveStatusChip}
              deployMessage={deployMessage}
              deployError={deployError}
              deploySeverity={deploySeverity}
              onClearDeployFeedback={() => {
                setDeployError(null);
                setDeployMessage(null);
                setDeploySeverity('info');
              }}
            />

            <Paper sx={{ p: 1.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle2">Screens</Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={handleAddScreen}>
                  Add
                </Button>
              </Stack>
              <Stack spacing={0.75}>
                {screens.map((s) => (
                  <Chip
                    key={s.id}
                    size="small"
                    label={s.name}
                    color={s.id === currentScreen?.id ? 'primary' : 'default'}
                    variant={s.id === currentScreen?.id ? 'filled' : 'outlined'}
                    onClick={() => handleSelectScreen(s.id)}
                    sx={{ justifyContent: 'flex-start' }}
                  />
                ))}
              </Stack>
            </Paper>
          </Stack>
        </Box>
      </Box>

      <TouchPanelButtonModal
        open={!!editingButton}
        button={editingButton}
        panel={panel}
        tenantId={tenantId}
        onClose={() => setEditingButton(null)}
        onSave={handleSaveButton}
        onDelete={() => handleDeleteButton()}
      />

      <AssignDevicesDialog
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        tenantId={tenantId}
        userId={userId}
        panel={panel}
        onChanged={refreshAssignments}
      />
    </MainLayout>
  );
}
